import { CartOfferLine, CartSellerLine, CartState } from '../models';
import { ShoppingCartActionTypes, ShoppingCartsActions } from './shopping-cart.actions';

export const initialState: CartState = {
  payload: {
    currency: '',
    sellers: [],
    offers: [],
    totals: null,
    discountCode: null,
  },
  loading: false,
  error: null,
};
/* NOSONAR */
export function reducer(state = initialState, action: ShoppingCartsActions): CartState {
// NOSONAR
  switch (action.type) {
    case ShoppingCartActionTypes.APPLY_DISCOUNT_CODE:
      return {
        payload: {
          ...state.payload,
          discountCode: {
            ...state.payload.discountCode,
            loading: true,
          },
        },
        loading: false,
        error: null,
      };

    case ShoppingCartActionTypes.READ_PARTIAL_SHOPPING_CART:
      return {
        ...state,
        loading: false,
        error: null,
      };

    case ShoppingCartActionTypes.READ_SHOPPING_CART:
    case ShoppingCartActionTypes.PROCEED_TO_CHECKOUT:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ShoppingCartActionTypes.UPDATE_CART:
      return {
        ...initialState,
        loading: true,
        error: null,
      };

    case ShoppingCartActionTypes.APPLY_DISCOUNT_CODE_SUCCESS:
    case ShoppingCartActionTypes.READ_SHOPPING_CART_SUCCESS:
    case ShoppingCartActionTypes.UPDATE_CART_SUCCESS:
      return {
        payload: {
          ...action.payload,
          discountCode: {
            ...state.payload.discountCode,
            ...action.payload.discountCode,
            loading: false,
          },
        },
        loading: false,
        error: null,
      };

    case ShoppingCartActionTypes.APPLY_DISCOUNT_CODE_FAILURE:
      return {
        payload: {
          ...state.payload,
          discountCode: {
            ...state.payload.discountCode,
            loading: false,
          },
        },
        loading: false,
        error: action.payload,
      };

    case ShoppingCartActionTypes.READ_SHOPPING_CART_FAILURE:
    case ShoppingCartActionTypes.PROCEED_TO_CHECKOUT_FAILURE:
    case ShoppingCartActionTypes.UPDATE_CART_FAILURE:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            return {
              ...offerLine,
              loading: false,
            };
          }),
        },
        loading: false,
        error: action.payload,
      };

    case ShoppingCartActionTypes.DELETE_DISCOUNT_CODE:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            return {
              ...offerLine,
              price: {
                ...offerLine.price,
                discountedPrice: null,
                discountedAmount: null,
                discountedNetAmount: null,
                discountedTaxPrice: null,
                discountedTaxAmount: null,
                discountedNetPrice: null,
              },
            };
          }),
          totals: {
            ...state.payload.totals,
            discountedPrice: null,
            discountedAmount: null,
            discountedNetAmount: null,
            discountedTaxPrice: null,
            discountedTaxAmount: null,
            discountedNetPrice: null,
          },
          discountCode: null,
        },
      };

    case ShoppingCartActionTypes.UPDATE_QUANTITY:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload.cartLine.offer) {
              return offerLine;
            }

            return {
              ...offerLine,
              quantity: action.payload.quantity,
              loading: true,
              deleting: false,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload.offer) {
              return offerLine;
            }

            const { ...updatedOfferLine } = action.payload;

            return {
              ...offerLine,
              ...updatedOfferLine,
              loading: false,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.UPDATE_QUANTITY_FAILURE:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload.id) {
              return offerLine;
            }

            return {
              ...offerLine,
              loading: false,
              error: action.payload.error,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.DELETE_PRODUCT:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload.id) {
              return offerLine;
            }

            return {
              ...offerLine,
              loading: true,
              deleting: true,
              error: null,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.DELETE_PRODUCT_FROM_MINI_CART:
      return {
        ...state,
      };

    case ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.filter((offerLine: CartOfferLine) => offerLine.offer !== action.payload.id),
          sellers: state.payload.sellers.reduce((sellerLines: CartSellerLine[], sellerLine: CartSellerLine) => {
            if (!sellerLine.items.includes(action.payload.id)) {
              return [...sellerLines, sellerLine];
            }

            if (sellerLine.items.length === 1) {
              return sellerLines;
            }

            return [
              ...sellerLines,
              {
                seller: sellerLine.seller,
                items: sellerLine.items.filter((offer: string) => offer !== action.payload.id),
                sellerTotals: sellerLine.sellerTotals,
                maxShippingTimeTotal: sellerLine.maxShippingTimeTotal,
                minShippingTimeTotal: sellerLine.minShippingTimeTotal,
              },
            ];
          }, []),
        },
      };

    case ShoppingCartActionTypes.DELETE_VALUE_ADDED_SERVICE:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload?.offerId) {
              return offerLine;
            }

            return {
              ...offerLine,
              loading: true,
              error: null,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.DELETE_VALUE_ADDED_SERVICE_SUCCESS:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            return {
              ...offerLine,
              ...(action.payload?.offerId === offerLine.offer
                ? {
                    valueAddedServices: offerLine?.valueAddedServices.filter((vas) => vas.id !== action.payload.vasId),
                  }
                : { valueAddedServices: { ...offerLine.valueAddedServices } }),
              loading: false,
              error: null,
            };
          }),
        },
      };

    case ShoppingCartActionTypes.DELETE_VALUE_ADDED_SERVICE_FAILURE:
    case ShoppingCartActionTypes.DELETE_PRODUCT_FAILURE:
      return {
        ...state,
        payload: {
          ...state.payload,
          offers: state.payload.offers.map((offerLine: CartOfferLine) => {
            if (offerLine.offer !== action.payload.id) {
              return offerLine;
            }

            return {
              ...offerLine,
              loading: false,
              deleting: false,
              error: action.payload.error,
            };
          }),
        },
      };

    default:
      return state;
  }
}
